import { useState, useEffect, useRef, useCallback } from "react"
import { X, Pencil, Bold, Italic, List, ListOrdered, Heading3, Check } from "lucide-react"
import { Button } from "components/ui/button"
import { updateCurrentUser } from "services/users"
import { useUser } from "contexts/UserContext"

interface NotePanelProps {
  onClose: () => void
}

export function NotePanel({ onClose }: NotePanelProps) {
  const { user, loading: userLoading, refreshUser } = useUser()
  const editorRef = useRef<HTMLDivElement>(null)
  const [initialized, setInitialized] = useState(false)
  const [charCount, setCharCount] = useState(0)
  const [showSaved, setShowSaved] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)
  const savedIndicatorTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Initialize editor with notes from context
  useEffect(() => {
    if (!userLoading && user && editorRef.current && !initialized) {
      const notes = (user as any).notes || ""
      editorRef.current.innerHTML = notes
      updateCharCount()
      setInitialized(true)
    }
  }, [user, userLoading, initialized])

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current)
      if (savedIndicatorTimeoutRef.current) clearTimeout(savedIndicatorTimeoutRef.current)
    }
  }, [])

  const updateCharCount = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || ""
      setCharCount(text.length)
    }
  }

  const saveNotes = useCallback(async () => {
    if (!editorRef.current) return

    const html = editorRef.current.innerHTML
    setIsSaving(true)

    try {
      await updateCurrentUser({ notes: html })
      // Refresh user context to sync notes across all components
      await refreshUser()
      setShowSaved(true)

      // Hide saved indicator after 3 seconds
      if (savedIndicatorTimeoutRef.current) {
        clearTimeout(savedIndicatorTimeoutRef.current)
      }
      savedIndicatorTimeoutRef.current = setTimeout(() => {
        setShowSaved(false)
      }, 3000)
    } catch (error) {
      console.error("Error saving notes:", error)
    } finally {
      setIsSaving(false)
    }
  }, [refreshUser])

  const handleInput = () => {
    updateCharCount()

    // Debounced auto-save (1.5 seconds)
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }
    saveTimeoutRef.current = setTimeout(() => {
      saveNotes()
    }, 1500)
  }

  const execCommand = (command: string, value: string | undefined = undefined) => {
    document.execCommand(command, false, value)
    editorRef.current?.focus()
  }

  const handleBold = () => execCommand("bold")
  const handleItalic = () => execCommand("italic")
  const handleUnorderedList = () => execCommand("insertUnorderedList")
  const handleOrderedList = () => execCommand("insertOrderedList")

  const handleHeading = () => {
    const selection = window.getSelection()
    if (selection && selection.rangeCount > 0) {
      const parentElement = selection.anchorNode?.parentElement
      const isH3 = parentElement?.tagName === "H3" || parentElement?.closest("h3")

      if (isH3) {
        execCommand("formatBlock", "p")
      } else {
        execCommand("formatBlock", "h3")
      }
    } else {
      execCommand("formatBlock", "h3")
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Ctrl+B for bold
    if (e.ctrlKey && e.key === "b") {
      e.preventDefault()
      handleBold()
    }
    // Ctrl+I for italic
    if (e.ctrlKey && e.key === "i") {
      e.preventDefault()
      handleItalic()
    }
  }

  // Sessione scaduta / utente non disponibile: mostra errore invece di "Caricamento" infinito
  if (!userLoading && !user) {
    return (
      <div
        className="flex flex-col h-full bg-[var(--hexa-surface-elevated)] items-center justify-center p-6"
        style={{ color: "var(--hexa-text-muted)" }}
      >
        <p className="text-center text-sm">
          Sessione scaduta. Effettua nuovamente il login.
        </p>
      </div>
    )
  }

  // Loading depends only on the user fetch. The editor mounts unconditionally
  // so the init effect can populate it via editorRef without chicken-and-egg.
  const loading = userLoading

  return (
    <div className="flex flex-col h-full bg-[var(--hexa-surface-elevated)]">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[var(--hexa-border)] px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Pencil className="h-5 w-5" style={{ color: "var(--hexa-purple)" }} />
            <h2 className="text-lg font-semibold" style={{ color: "var(--hexa-text-primary)" }}>
              Note
            </h2>
          </div>
          <div className="flex items-center gap-3">
            {/* Saved indicator */}
            <div
              className="flex items-center gap-1.5 transition-opacity duration-300"
              style={{
                opacity: showSaved ? 1 : 0,
                color: "var(--hexa-success)",
                fontSize: 13,
              }}
            >
              <Check className="h-4 w-4" />
              <span>Salvato</span>
            </div>
            {/* Close button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto scrollbar-hexa p-6 flex flex-col gap-4">
        {/* Toolbar */}
        <div
          className="flex items-center gap-1 p-2 rounded-lg"
          style={{ backgroundColor: "var(--hexa-border-subtle)" }}
        >
          {/* Text formatting */}
          <ToolbarButton onAction={handleBold} title="Grassetto (Ctrl+B)">
            <Bold className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onAction={handleItalic} title="Corsivo (Ctrl+I)">
            <Italic className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          {/* Lists */}
          <ToolbarButton onAction={handleUnorderedList} title="Elenco puntato">
            <List className="h-4 w-4" />
          </ToolbarButton>
          <ToolbarButton onAction={handleOrderedList} title="Elenco numerato">
            <ListOrdered className="h-4 w-4" />
          </ToolbarButton>

          <ToolbarSeparator />

          {/* Heading */}
          <ToolbarButton onAction={handleHeading} title="Titoletto">
            <Heading3 className="h-4 w-4" />
          </ToolbarButton>
        </div>

        {/* Editor - sempre renderizzato così l'init effect può popolarlo */}
        <div className="flex-1 relative">
          <div
            ref={editorRef}
            contentEditable={!loading}
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            className="note-editor w-full h-full min-h-[300px] outline-none"
            style={{
              fontSize: 14,
              lineHeight: 1.7,
              color: "var(--hexa-text-primary)",
              opacity: loading ? 0 : 1,
            }}
            data-placeholder="Scrivi le tue note..."
          />
          {loading && (
            <div
              className="absolute inset-0 flex items-center justify-center pointer-events-none"
              style={{ color: "var(--hexa-text-secondary)" }}
            >
              Caricamento...
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex-shrink-0 border-t border-[var(--hexa-border)] px-6 py-3">
        <div className="flex items-center justify-between">
          {/* Character count */}
          <span style={{ fontSize: 11, color: "var(--hexa-text-muted)" }}>
            {charCount} caratteri
          </span>

          {/* Disabled buttons */}
          <div className="flex items-center gap-2">
            <button
              disabled
              className="px-3 py-1.5 rounded-md border"
              style={{
                fontSize: 11,
                borderColor: "var(--hexa-border)",
                color: "var(--hexa-text-muted)",
                opacity: 0.5,
                cursor: "not-allowed",
              }}
            >
              Scarica
            </button>
            <button
              disabled
              className="px-3 py-1.5 rounded-md border"
              style={{
                fontSize: 11,
                borderColor: "var(--hexa-border)",
                color: "var(--hexa-text-muted)",
                opacity: 0.5,
                cursor: "not-allowed",
              }}
            >
              Condividi
            </button>
          </div>
        </div>
      </div>

      {/* Editor styles */}
      <style>{`
        .note-editor:empty:before {
          content: attr(data-placeholder);
          color: var(--hexa-text-muted);
          pointer-events: none;
        }
        .note-editor h3 {
          font-size: 15px;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .note-editor ul,
        .note-editor ol {
          padding-left: 1.5em;
          margin: 0.5em 0;
        }
        .note-editor li::marker {
          color: var(--hexa-purple);
        }
        .note-editor p {
          margin: 0.25em 0;
        }
      `}</style>
    </div>
  )
}

// Toolbar button component
function ToolbarButton({
  onAction,
  title,
  children,
}: {
  onAction: () => void
  title: string
  children: React.ReactNode
}) {
  return (
    <button
      // Use onMouseDown with preventDefault to keep editor selection
      onMouseDown={(e) => {
        e.preventDefault()
        onAction()
      }}
      title={title}
      className="flex items-center justify-center transition-colors"
      style={{
        width: 34,
        height: 34,
        borderRadius: 7,
        color: "var(--hexa-text-muted)",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = "rgba(106, 77, 255, 0.1)"
        e.currentTarget.style.color = "var(--hexa-purple)"
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = "transparent"
        e.currentTarget.style.color = "var(--hexa-text-muted)"
      }}
    >
      {children}
    </button>
  )
}

// Toolbar separator component
function ToolbarSeparator() {
  return (
    <div
      className="mx-1"
      style={{
        width: 1,
        height: 20,
        backgroundColor: "var(--hexa-border)",
      }}
    />
  )
}
